//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Control,
  FormProvider,
  UseFormSetValue,
  UseFormWatch,
  useForm,
} from 'react-hook-form';
import { useSelector } from 'react-redux';

import {
  Retailer,
  SamplingCampaignRequest,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import {
  ClientRetailerWithId,
  GetClientRetailersResponse,
} from '@apis/campaign-requests/sampling/retailers/samplingRetailersApiModel';
import {
  useDeleteClientCampaignRequestMutation,
  useEditClientCampaignRequestMutation,
  useGetClientRetailersQuery,
  useRequestQuoteMutation,
  useSamplingCampaignRequestQuery,
} from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { SamplingCampaignRequestParams } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsTypes';
import { CreateRedemptionLocationModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/fulfillment-section/deals/create-redemption-location/CreateRedemptionLocationModal';
import { BulkUploadModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/products-section/bulk-upload-modal/BulkUploadModal';
import { RequestCampaignModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/standard/RequestCampaignModal';
import { RequestCampaignSuccessModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/standard/RequestCampaignSuccessModal';
import { SamplingRequestSubmitErrorsModal } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/standard/SamplingRequestSubmitErrorsModal';
import { SamplingCampaignRequestSectionName } from '@campaigns/constants/sampling/SamplingCampaignRequestSectionConstants';
import { ProductModalProvider } from '@campaigns/contexts/ProductModalContext';
import {
  hasProductsWithMissingDetails,
  validateForRequest,
} from '@campaigns/utils/sampling/SamplingCampaignRequestValidationRulesUtils';
import { mergeProductsInCampaignRequest } from '@campaigns/utils/sampling/SamplingProductUtils';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { getCurrentUser } from '@redux/selectors/getSessionInfo';
import { isDefined, isTruthy } from '@utils/filterPredicates';
import { getRequestErrorCode } from '@utils/getRequestErrorCode';

import { RoutableStateName } from 'app/top-level-states';

interface SamplingCampaignContextProps {
  formControl: Control<SamplingCampaignRequest>;
  isAutoSaving: boolean;
  isDeleteError: boolean;
  isDeleting: boolean;
  isError: boolean;
  isLoading: boolean;
  isRedemptionLocationModalOpen: boolean;
  isRequestContactModalOpen: boolean;
  isQuoting: boolean;
  isCreateQuoteError: boolean;
  isRequesting: boolean;
  isSaveError: boolean;
  isSaving: boolean;
  isSubmitErrorsModalOpen: boolean;
  redemptionLocationIndex: number | undefined;
  redemptionLocationToEdit: Retailer | undefined;
  samplingCampaignRequest: SamplingCampaignRequest | undefined;
  validationSubmitErrors: Map<SamplingCampaignRequestSectionName, string[]>;
  closeRedemptionLocationModal: () => void;
  closeSubmitErrorsModal: () => void;
  deleteLocation: (index: number) => void;
  handleAutoSave: (targetState: RoutableStateName) => void;
  handleDeleteCampaignRequest: () => Promise<void>;
  handleRequestCampaign: () => void;
  handleRequestContactCampaign: () => Promise<void>;
  handleRequestQuote: () => Promise<void>;
  handleSubmitCampaign: () => Promise<void>;
  handleRevertToDraft: () => Promise<void>;
  handleSave: (
    shouldNavigate?: boolean,
    updatedCampaignRequest?: SamplingCampaignRequest,
  ) => Promise<void>;
  openBulkUploadModal: () => void;
  openRedemptionLocationModal: (index?: number) => void;
  openRequestContactModal: () => void;
  saveLocation: (data: Retailer) => void;
  watchFormValues: UseFormWatch<SamplingCampaignRequest>;
  updateFormValues: UseFormSetValue<SamplingCampaignRequest>;
  canAddProductsToCatalog: boolean;
  selectedRetailer: ClientRetailerWithId | undefined;
}

const defaultContext: SamplingCampaignContextProps = {
  canAddProductsToCatalog: false,
  isAutoSaving: false,
  isDeleteError: false,
  isDeleting: false,
  isError: false,
  isLoading: false,
  isRedemptionLocationModalOpen: false,
  isRequestContactModalOpen: false,
  isQuoting: false,
  isCreateQuoteError: false,
  isRequesting: false,
  isSaveError: false,
  isSaving: false,
  isSubmitErrorsModalOpen: false,
  redemptionLocationIndex: undefined,
  redemptionLocationToEdit: undefined,
  samplingCampaignRequest: undefined,
  selectedRetailer: undefined,
  validationSubmitErrors: new Map(),
  closeRedemptionLocationModal: () => undefined,
  closeSubmitErrorsModal: () => undefined,
  deleteLocation: () => undefined,
  handleAutoSave: () => undefined,
  handleDeleteCampaignRequest: async () => Promise.resolve(),
  handleRequestCampaign: () => undefined,
  handleRequestContactCampaign: async () => Promise.resolve(),
  handleRequestQuote: async () => Promise.resolve(),
  handleSubmitCampaign: async () => Promise.resolve(),
  handleRevertToDraft: async () => Promise.resolve(),
  handleSave: async () => Promise.resolve(),
  openBulkUploadModal: () => undefined,
  openRedemptionLocationModal: () => undefined,
  openRequestContactModal: () => undefined,
  saveLocation: () => undefined,
  formControl: {} as Control<SamplingCampaignRequest>,
  watchFormValues: (() =>
    undefined) as unknown as UseFormWatch<SamplingCampaignRequest>,
  updateFormValues: () => undefined,
};

const SamplingCampaignContext =
  createContext<SamplingCampaignContextProps>(defaultContext);

export const SamplingCampaignProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Hooks
  const { params } = useCurrentStateAndParams<SamplingCampaignRequestParams>();
  const { data, error, isError, isLoading } = useSamplingCampaignRequestQuery(
    {
      campaignId: params.campaignId,
    },
    (data) => ({
      ...data,
      targetClientId: isDefined(data.requestorClientId)
        ? data.bvClientId
        : undefined,
    }),
  );

  // State
  const [redemptionLocationToEdit, setRedemptionLocationToEdit] = useState<
    Retailer | undefined
  >(undefined);
  const [redemptionLocationIndex, setRedemptionLocationIndex] = useState<
    number | undefined
  >(undefined);
  const [
    isRequestCampaignModalSuccessOpen,
    setIsRequestCampaignModalSuccessOpen,
  ] = useState(false);
  const [isRequestCampaignModalOpen, setIsRequestCampaignModalOpen] =
    useState(false);
  const [isRequestContactModalOpen, setIsRequestContactModalOpen] =
    useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isRedemptionLocationModalOpen, setIsRedemptionLocationModalOpen] =
    useState(false);
  const [isSubmitErrorsModalOpen, setIsSubmitErrorsModalOpen] = useState(false);
  const [validationSubmitErrors, setSubmitValidationErrors] = useState<
    Map<SamplingCampaignRequestSectionName, string[]>
  >(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false); // TODO: PD-251368 - [FE] Implement section check before request
  const [isQuoting, setIsQuoting] = useState(false);
  const [isCreateQuoteError, setIsCreateQuoteError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const redemptionLocationFormMethods = useForm<Retailer>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {},
  });

  const { isError: isSaveError, mutateAsync: editCampaignRequestAsync } =
    useEditClientCampaignRequestMutation();

  const { isError: isDeleteError, mutateAsync: deleteCampaignRequestAsync } =
    useDeleteClientCampaignRequestMutation();

  const { mutateAsync: requestQuoteAsync } = useRequestQuoteMutation();

  const { email, firstName, lastName } = useSelector(getCurrentUser);

  const formMethods = useForm<SamplingCampaignRequest>({
    defaultValues: data,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const { control, getValues, reset, setValue, watch } = formMethods;

  const getSelectedRetailer = (
    retailersResponse: GetClientRetailersResponse | undefined,
  ): ClientRetailerWithId | undefined => {
    const { requestorClientId, targetClientId } = getValues();

    if (
      !isDefined(retailersResponse?.results) ||
      !isDefined(requestorClientId) ||
      !isDefined(targetClientId)
    ) {
      return undefined;
    }

    // Find the retailer whose client ID matches the target ID from the form.
    return retailersResponse.results.find(
      (retailer) => retailer.bvClientId === targetClientId,
    );
  };

  const { data: selectedRetailer } =
    useGetClientRetailersQuery(getSelectedRetailer);

  const canAddProductsToCatalog = selectedRetailer
    ? isTruthy(selectedRetailer.productCreationEnabled)
    : true;

  useEffect(() => {
    if (isError) {
      const errorCode = getRequestErrorCode(error);
      if (errorCode === 404 || errorCode === 403) {
        void navigateToState('campaigns.sampling');
      }
    }
  }, [isError, error]);

  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data, reset]);

  // Handlers
  const closeSubmitErrorsModal = () => {
    setIsSubmitErrorsModalOpen(false);
  };

  const handleRevertToDraft = async () => {
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsSaving(true);
      try {
        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: {
            ...formValues,
            status: 'draft',
          },
        });
        void navigateToState(
          'campaigns.sampling-campaign-request-details-edit',
          {
            campaignId: formValues.campaignRequestId,
          },
        );
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleDeleteCampaignRequest = async () => {
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsDeleting(true);

      try {
        await deleteCampaignRequestAsync({
          id: formValues.campaignRequestId,
        });
        void navigateToState('campaigns.sampling');
      } catch (error) {
        // Error will be handled by isDeleteError state
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleAutoSave = async (targetState: RoutableStateName) => {
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsAutoSaving(true);

      try {
        // Trigger form validation
        const isValid = await formMethods.trigger();
        const productErrors = hasProductsWithMissingDetails(formValues);

        if (!isValid || productErrors.size > 0) {
          // If form is not valid, don't proceed with saving
          return;
        }

        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: formValues,
        });

        void navigateToState(targetState, {
          campaignId: formValues.campaignRequestId,
        });
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsAutoSaving(false);
      }
    }
  };

  const handleSave = async (shouldNavigate: boolean = true): Promise<void> => {
    const formValues = getValues();

    if (formValues.campaignRequestId) {
      setIsSaving(true);

      try {
        const productErrors = hasProductsWithMissingDetails(formValues);
        if (productErrors.size > 0) {
          setSubmitValidationErrors(productErrors);
          setIsSubmitErrorsModalOpen(true);
        } else {
          await editCampaignRequestAsync({
            id: formValues.campaignRequestId,
            campaign: {
              ...formValues,
              comments: formValues.comments ?? {},
            },
          });
          if (shouldNavigate) {
            void navigateToState('campaigns.sampling');
          }
        }
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleRequestCampaign = () => {
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      const errors = validateForRequest(formValues);
      if (errors.size > 0) {
        setSubmitValidationErrors(errors);
        setIsSubmitErrorsModalOpen(true);
      } else {
        setIsRequestCampaignModalOpen(true);
      }
    }
  };

  const handleRequestQuote = async () => {
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      const errors = validateForRequest(formValues);
      if (errors.size > 0) {
        setSubmitValidationErrors(errors);
        setIsSubmitErrorsModalOpen(true);
      } else {
        setIsQuoting(true);
        setIsCreateQuoteError(false);

        try {
          await editCampaignRequestAsync({
            id: formValues.campaignRequestId,
            campaign: {
              ...formValues,
              comments: formValues.comments ?? {},
            },
          });

          await requestQuoteAsync({
            campaignRequestId: formValues.campaignRequestId,
            payload: {
              quoteContact: {
                email,
                firstName,
                lastName,
              },
            },
          });

          void navigateToState('campaigns.sampling-campaign-request-details', {
            campaignId: formValues.campaignRequestId,
          });
        } catch (error) {
          setIsCreateQuoteError(true);
          // Error will be handled by isSaveError state
        } finally {
          setIsQuoting(false);
        }
      }
    }
  };

  const handleSubmitCampaign = async () => {
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsRequesting(true);

      try {
        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: {
            ...formValues,
            status: 'submitted',
          },
        });

        setIsRequestCampaignModalOpen(false);
        setIsRequestCampaignModalSuccessOpen(true);
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsRequesting(false);
      }
    }
  };

  const handleRequestContactCampaign = async () => {
    const formValues = getValues();
    if (formValues.campaignRequestId) {
      setIsRequesting(true);
      try {
        await editCampaignRequestAsync({
          id: formValues.campaignRequestId,
          campaign: {
            ...formValues,
            status: 'submitted',
          },
        });
        setIsRequestContactModalOpen(false);
        setIsRequestCampaignModalSuccessOpen(true);
      } catch (error) {
        // Error will be handled by isSaveError state
      } finally {
        setIsRequesting(false);
      }
    }
  };

  const saveLocation = (data: Retailer) => {
    const formValues = getValues();
    const updatedRetailers = [
      ...(formValues.fulfillmentOptions?.retailers ?? []),
    ];
    if (redemptionLocationIndex !== undefined) {
      updatedRetailers[redemptionLocationIndex] = data;
    } else {
      updatedRetailers.push(data);
    }
    setValue('fulfillmentOptions.retailers', updatedRetailers);
  };

  const deleteLocation = (index: number) => {
    const formValues = getValues();
    const updatedRetailers = [
      ...(formValues.fulfillmentOptions?.retailers ?? []),
    ].filter((_, i) => i !== index);
    setValue('fulfillmentOptions.retailers', updatedRetailers);
  };

  const addProductToCampaign = (
    product: BVSamplingProduct,
    isEdit: boolean,
  ) => {
    const currentProducts = getValues().products ?? [];
    const updatedProducts = isEdit
      ? currentProducts.map((p) =>
          p.targetCatalogProductId === product.targetCatalogProductId
            ? product
            : p,
        )
      : [...currentProducts, product];
    setValue('products', updatedProducts);
  };

  const openRedemptionLocationModal = (index?: number) => {
    const formValues = getValues();
    setIsRedemptionLocationModalOpen(true);
    const retailer =
      isDefined(index) && formValues.fulfillmentOptions?.retailers
        ? formValues.fulfillmentOptions.retailers[index]
        : undefined;
    setRedemptionLocationToEdit(retailer);
    setRedemptionLocationIndex(index);
    redemptionLocationFormMethods.reset(retailer);
  };

  const openBulkUploadModal = () => {
    setIsBulkUploadModalOpen(true);
  };

  const openRequestContactModal = () => {
    setIsRequestContactModalOpen(true);
  };

  const closeRedemptionLocationModal = () => {
    setIsRedemptionLocationModalOpen(false);
    setRedemptionLocationToEdit(undefined);
    redemptionLocationFormMethods.reset({});
  };

  const closeRequestCampaignSuccessModal = () => {
    setIsRequestCampaignModalSuccessOpen(false);
    const campaignRequestId = getValues().campaignRequestId;

    if (isDefined(campaignRequestId)) {
      // Navigate when user closes success modal
      void navigateToState('campaigns.sampling-campaign-request-details', {
        campaignId: campaignRequestId,
      });
    }
  };

  const handleProductsImported = (newProducts: BVSamplingProduct[]) => {
    const formValues = getValues();
    const mergedProducts = mergeProductsInCampaignRequest(
      formValues.products,
      newProducts,
    );
    setValue('products', mergedProducts);
  };

  return (
    <SamplingCampaignContext.Provider
      value={{
        formControl: control,
        isAutoSaving,
        isDeleteError,
        isDeleting,
        isError,
        isLoading,
        isRedemptionLocationModalOpen,
        isRequestContactModalOpen,
        isQuoting,
        isCreateQuoteError,
        isRequesting,
        isSaveError: isSaveError || isCreateQuoteError,
        isSaving,
        isSubmitErrorsModalOpen,
        redemptionLocationIndex,
        redemptionLocationToEdit,
        samplingCampaignRequest: watch(),
        validationSubmitErrors,
        closeRedemptionLocationModal,
        closeSubmitErrorsModal,
        deleteLocation,
        handleAutoSave,
        handleDeleteCampaignRequest,
        handleRequestCampaign,
        handleRequestContactCampaign,
        handleRequestQuote,
        handleSubmitCampaign,
        handleRevertToDraft,
        handleSave,
        openBulkUploadModal,
        openRedemptionLocationModal,
        openRequestContactModal,
        saveLocation,
        updateFormValues: setValue,
        watchFormValues: watch,
        canAddProductsToCatalog,
        selectedRetailer,
      }}
    >
      <ProductModalProvider addProductToCampaign={addProductToCampaign}>
        <FormProvider {...formMethods}>
          <SamplingRequestSubmitErrorsModal />
          <FormProvider {...redemptionLocationFormMethods}>
            <CreateRedemptionLocationModal />
          </FormProvider>
          <RequestCampaignModal
            isOpen={isRequestCampaignModalOpen}
            onClose={() => setIsRequestCampaignModalOpen(false)}
            onSubmit={handleSubmitCampaign}
          />
          <RequestCampaignSuccessModal
            isOpen={isRequestCampaignModalSuccessOpen}
            onClose={closeRequestCampaignSuccessModal}
          />
          <BulkUploadModal
            closeModal={() => setIsBulkUploadModalOpen(false)}
            isModalOpen={isBulkUploadModalOpen}
            onProductsImported={handleProductsImported}
          />
          {children}
        </FormProvider>
      </ProductModalProvider>
    </SamplingCampaignContext.Provider>
  );
};

export const useSamplingCampaign = () => useContext(SamplingCampaignContext);
